import React from 'react';
import {
    FormControl,
    TextField,
    MenuItem,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Button,
    Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkflowSessionModalButton from './WorkflowSessionModalButton';
import SmartPasswordField from '../../../../../commonComponents/SmartPasswordField';

const GRANT_TYPES = [
    { value: 'AUTHORIZATION_CODE', labelKey: 'OAUTH2_GRANT_AUTH_CODE' },
    { value: 'AUTHORIZATION_CODE_PKCE', labelKey: 'OAUTH2_GRANT_AUTH_CODE_PKCE' },
    // { value: 'IMPLICIT', labelKey: 'OAUTH2_GRANT_IMPLICIT' }, // not supported: token only in URL fragment
    { value: 'PASSWORD_CREDENTIALS', labelKey: 'OAUTH2_GRANT_PASSWORD_CREDENTIALS' },
    { value: 'CLIENT_CREDENTIALS', labelKey: 'OAUTH2_GRANT_CLIENT_CREDENTIALS' },
];

const CLIENT_AUTH_OPTIONS = [
    { value: 'SEND_IN_BODY', labelKey: 'OAUTH2_CLIENT_AUTH_SEND_IN_BODY' },
    { value: 'SEND_AS_BASIC_HEADER', labelKey: 'OAUTH2_CLIENT_AUTH_AS_BASIC' },
];

export const AddToOptions = [
    { value: 'REQUEST_HEADER', labelKey: 'OAUTH2_ADD_TO_REQUEST_HEADER' },
    { value: 'QUERY_PARAM', labelKey: 'OAUTH2_ADD_TO_QUERY_PARAM' },
];

function parseQueryToMap(queryString, sink) {
    if (!queryString) return;
    queryString.split('&').forEach((pair) => {
        const eq = pair.indexOf('=');
        if (eq <= 0) return;
        const k = decodeURIComponent(pair.substring(0, eq)).trim();
        const v = decodeURIComponent(pair.substring(eq + 1));
        if (k) sink[k] = v;
    });
}

function parseAdditionalAuthorizationParams(raw, sink) {
    if (!raw || !raw.trim()) return;
    raw.trim().split(/\r?\n/).forEach((line) => {
        line.split('&').forEach((pair) => {
            const p = pair.trim();
            if (!p) return;
            const eq = p.indexOf('=');
            if (eq <= 0) return;
            const k = p.substring(0, eq).trim();
            const v = p.substring(eq + 1).trim();
            if (k) sink[k] = v;
        });
    });
}

function encodeQueryMap(params) {
    return Object.keys(params)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k] ?? '')}`)
        .join('&');
}

function generateOAuthState() {
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    let s = '';
    a.forEach((b) => { s += String.fromCharCode(b); });
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function validateRedirectUriFormat(redirectUriRaw) {
    if (!redirectUriRaw || !String(redirectUriRaw).trim()) {
        return 'ERR_OAUTH2_REDIRECT_URI_MISSING';
    }
    try {
        const u = new URL(String(redirectUriRaw).trim());
        if (!u.protocol || (!u.protocol.startsWith('http:') && !u.protocol.startsWith('https:'))) {
            return 'ERR_OAUTH2_REDIRECT_URI_INVALID';
        }
        if (!u.host) return 'ERR_OAUTH2_REDIRECT_URI_INVALID';
    } catch {
        return 'ERR_OAUTH2_REDIRECT_URI_INVALID';
    }
    return null;
}

/** Mirrors {@link com.oidev.bridgegate.adapter.HttpOAuth2Support#buildAuthorizationCodeAuthorizeUrl} */
async function buildAuthorizationCodeAuthorizeUrl(baseAuthorizeUrl, clientId, redirectUri, scope, state, usePkce, codeVerifier, additionalParamsRaw) {
    const base = (baseAuthorizeUrl || '').trim();
    if (!base) return { url: '', stateUsed: '' };
    const q = base.indexOf('?');
    const pathPart = q >= 0 ? base.substring(0, q) : base;
    const params = {};
    if (q >= 0 && q < base.length - 1) {
        parseQueryToMap(base.substring(q + 1), params);
    }
    parseAdditionalAuthorizationParams(additionalParamsRaw, params);
    if (clientId && clientId.trim()) params.client_id = clientId.trim();
    if (redirectUri && redirectUri.trim()) params.redirect_uri = redirectUri.trim();
    if (scope && scope.trim()) params.scope = scope.trim();
    params.response_type = 'code';
    const stateUsed = state && state.trim() ? state.trim() : generateOAuthState();
    params.state = stateUsed;
    if (usePkce && codeVerifier && codeVerifier.trim() && !params.code_challenge) {
        const ch = await pkceChallengeS256(codeVerifier.trim());
        params.code_challenge = ch;
        params.code_challenge_method = 'S256';
    }
    const query = encodeQueryMap(params);
    return { url: query ? `${pathPart}?${query}` : pathPart, stateUsed };
}

function randomPkceVerifier() {
    const a = new Uint8Array(32);
    crypto.getRandomValues(a);
    let s = '';
    a.forEach((b) => { s += String.fromCharCode(b); });
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function pkceChallengeS256(verifier) {
    const enc = new TextEncoder().encode(verifier);
    const buf = await crypto.subtle.digest('SHA-256', enc);
    const bytes = new Uint8Array(buf);
    let bin = '';
    bytes.forEach((b) => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

const InputRow = ({
    t,
    labelKey,
    value,
    onChange,
    keyName,
    disableControl,
    screen,
    setWorkflowSessionModal,
    setValueLabel,
    onInputChange,
    setter,
    fontWeights,
    fontWeightKey,
    type = 'text',
    password = false,
    wizardLabelKey,
}) => {
    const modalLabel = wizardLabelKey ? t(wizardLabelKey) : t(labelKey);
    const field = (
        <div style={{ display: 'flex', gap: '4px' }}>
            <FormControl className="width-100" size="small">
                {password ? (
                    <SmartPasswordField
                        variant="outlined"
                        size="small"
                        label={t(labelKey)}
                        value={value || ''}
                        disabled={disableControl || false}
                        inputProps={fontWeightKey && fontWeights ? { style: { fontWeight: fontWeights[fontWeightKey] } } : {}}
                        onChange={(e) => {
                            const v = e.target.value;
                            setter(v);
                            if (onChange) onChange(v);
                            else onInputChange(keyName, v);
                        }}
                        className="width-100"
                        name={keyName}
                        autoComplete="off"
                    />
                ) : (
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t(labelKey)}
                        type={type}
                        value={value || ''}
                        disabled={disableControl || false}
                        inputProps={fontWeightKey && fontWeights ? { style: { fontWeight: fontWeights[fontWeightKey] } } : {}}
                        onChange={(e) => {
                            if (onChange) onChange(e.target.value);
                            else { setter(e.target.value); onInputChange(keyName, e.target.value); }
                        }}
                        className="width-100"
                        name={keyName}
                        autoComplete="off"
                    />
                )}
            </FormControl>
            {['GET_DATA', 'SEND_DATA'].includes(screen) && (
                <WorkflowSessionModalButton
                    disableControl={disableControl || false}
                    label={modalLabel}
                    setWorkflowSessionModal={setWorkflowSessionModal}
                    setValueLabel={setValueLabel}
                />
            )}
        </div>
    );
    return field;
};

const OAuth2Fields = (props) => {
    const {
        t,
        disableControl,
        screen,
        onInputChange,
        setWorkflowSessionModal,
        setValueLabel,
        fontWeights = {},
        oauth2Token,
        setOauth2Token,
        oauth2HeaderPrefix,
        setOauth2HeaderPrefix,
        oauth2AutoRefresh,
        setOauth2AutoRefresh,
        oauth2AddTo,
        setOauth2AddTo,
        oauth2GrantType,
        setOauth2GrantType,
        oauth2AuthorizationUrl,
        setOauth2AuthorizationUrl,
        oauth2RedirectUri,
        setOauth2RedirectUri,
        oauth2AuthorizationCode,
        setOauth2AuthorizationCode,
        oauth2CodeVerifier,
        setOauth2CodeVerifier,
        oauth2AccessTokenUrl,
        setOauth2AccessTokenUrl,
        oauth2ClientId,
        setOauth2ClientId,
        oauth2ClientSecret,
        setOauth2ClientSecret,
        oauth2Username,
        setOauth2Username,
        oauth2Password,
        setOauth2Password,
        onOauth2PasswordChange,
        oauth2ClientAuth,
        setOauth2ClientAuth,
        oauth2Scope,
        setOauth2Scope,
        oauth2State,
        setOauth2State,
        oauth2RefreshTokenUrl,
        setOauth2RefreshTokenUrl,
        oauth2AuthorizationAdditionalParams,
        setOauth2AuthorizationAdditionalParams,
        onOauthDevNotify,
    } = props;

    const showAuthCodeUi = oauth2GrantType === 'AUTHORIZATION_CODE' || oauth2GrantType === 'AUTHORIZATION_CODE_PKCE';
    const showPkceVerifier = oauth2GrantType === 'AUTHORIZATION_CODE_PKCE';

    const buildBrowserUrl = async () => {
        const base = (oauth2AuthorizationUrl || '').trim();
        if (!base) return '';
        const pkce = oauth2GrantType === 'AUTHORIZATION_CODE_PKCE';
        const r = await buildAuthorizationCodeAuthorizeUrl(
            base,
            oauth2ClientId,
            oauth2RedirectUri,
            oauth2Scope,
            oauth2State,
            pkce,
            oauth2CodeVerifier,
            oauth2AuthorizationAdditionalParams,
        );
        return r.url;
    };

    const openAuthUrl = async () => {
        const base = (oauth2AuthorizationUrl || '').trim();
        if (!base) {
            if (onOauthDevNotify) onOauthDevNotify(t('ERR_OAUTH2_AUTH_URL_EMPTY'));
            return;
        }
        const invKey = validateRedirectUriFormat(oauth2RedirectUri);
        if (invKey) {
            if (onOauthDevNotify) onOauthDevNotify(t(invKey));
            return;
        }
        if (onOauthDevNotify) {
            const template = t('MSG_OAUTH2_OPEN_BROWSER_INFO');
            onOauthDevNotify(`${t('TIT_OAUTH2')}: ${template.replace(/\{0\}/g, oauth2RedirectUri)}`);
        }
        const u = await buildBrowserUrl();
        if (!u) return;
        window.open(u, '_blank', 'noopener,noreferrer');
    };

    const copyAuthUrl = async () => {
        const base = (oauth2AuthorizationUrl || '').trim();
        if (!base) return;
        const invKey = validateRedirectUriFormat(oauth2RedirectUri);
        if (invKey) {
            if (onOauthDevNotify) onOauthDevNotify(t(invKey));
            return;
        }
        const u = await buildBrowserUrl();
        if (u && navigator.clipboard) {
            await navigator.clipboard.writeText(u);
        }
    };

    const generatePkce = async () => {
        const v = randomPkceVerifier();
        setOauth2CodeVerifier(v);
        onInputChange('OAUTH2_CODE_VERIFIER', v);
    };

    const localRedirectInfo = () => {
        if (onOauthDevNotify) {
            onOauthDevNotify(t('MSG_OAUTH2_LOCAL_CALLBACK_INFO'));
        }
    };

    const grantOrderForSelect = [...GRANT_TYPES].sort((a, b) => {
        const order = ['PASSWORD_CREDENTIALS', 'CLIENT_CREDENTIALS', 'AUTHORIZATION_CODE', 'AUTHORIZATION_CODE_PKCE']; // 'IMPLICIT' omitted
        return order.indexOf(a.value) - order.indexOf(b.value);
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <InputRow
                t={t}
                labelKey="OAUTH2_TOKEN"
                value={oauth2Token}
                keyName="OAUTH2_TOKEN"
                disableControl={disableControl}
                screen={screen}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
                onInputChange={onInputChange}
                setter={setOauth2Token}
            />

            <InputRow
                t={t}
                labelKey="OAUTH2_HEADER_PREFIX"
                value={oauth2HeaderPrefix}
                keyName="OAUTH2_HEADER_PREFIX"
                disableControl={disableControl}
                screen={screen}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
                onInputChange={onInputChange}
                setter={setOauth2HeaderPrefix}
            />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: '0.875rem' }}>{t('OAUTH2_AUTO_REFRESH_TOKEN')}</span>
                <Switch
                    checked={!!oauth2AutoRefresh}
                    disabled={disableControl || false}
                    onChange={(e) => setOauth2AutoRefresh(e.target.checked)}
                    color="primary"
                    size="small"
                />
            </div>

            <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{t('OAUTH2_CONFIGURE_TOKEN')}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl className="width-100" size="small">
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={t('OAUTH2_GRANT_TYPE')}
                            value={oauth2GrantType || 'PASSWORD_CREDENTIALS'}
                            disabled={disableControl || false}
                            onChange={(e) => { setOauth2GrantType(e.target.value); onInputChange('OAUTH2_GRANT_TYPE', e.target.value); }}
                            className="width-100"
                        >
                            {grantOrderForSelect.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    {showAuthCodeUi && (
                        <>
                            <InputRow
                                t={t}
                                labelKey="OAUTH2_AUTHORIZATION_URL"
                                value={oauth2AuthorizationUrl}
                                keyName="OAUTH2_AUTHORIZATION_URL"
                                disableControl={disableControl}
                                screen={screen}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                                onInputChange={onInputChange}
                                setter={setOauth2AuthorizationUrl}
                            />
                            <InputRow
                                t={t}
                                labelKey="OAUTH2_REDIRECT_URI"
                                value={oauth2RedirectUri}
                                keyName="OAUTH2_REDIRECT_URI"
                                disableControl={disableControl}
                                screen={screen}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                                onInputChange={onInputChange}
                                setter={setOauth2RedirectUri}
                            />
                            <InputRow
                                t={t}
                                labelKey="OAUTH2_AUTHORIZATION_CODE"
                                value={oauth2AuthorizationCode}
                                keyName="OAUTH2_AUTHORIZATION_CODE"
                                disableControl={disableControl}
                                screen={screen}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                                onInputChange={onInputChange}
                                setter={setOauth2AuthorizationCode}
                            />
                            {showPkceVerifier && (
                                <InputRow
                                    t={t}
                                    labelKey="OAUTH2_CODE_VERIFIER"
                                    value={oauth2CodeVerifier}
                                    keyName="OAUTH2_CODE_VERIFIER"
                                    disableControl={disableControl}
                                    screen={screen}
                                    setWorkflowSessionModal={setWorkflowSessionModal}
                                    setValueLabel={setValueLabel}
                                    onInputChange={onInputChange}
                                    setter={setOauth2CodeVerifier}
                                />
                            )}
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                <Button size="small" variant="outlined" disabled={disableControl} onClick={openAuthUrl}>{t('BTN_OAUTH2_OPEN_AUTH_URL')}</Button>
                                <Button size="small" variant="outlined" disabled={disableControl} onClick={copyAuthUrl}>{t('BTN_OAUTH2_COPY_AUTH_URL')}</Button>
                                <Button size="small" variant="outlined" disabled={disableControl || !showPkceVerifier} onClick={generatePkce}>{t('BTN_OAUTH2_GENERATE_PKCE')}</Button>
                                <Button size="small" variant="outlined" disabled={disableControl} onClick={localRedirectInfo}>{t('BTN_OAUTH2_LOCAL_CALLBACK')}</Button>
                            </Stack>
                            <InputRow
                                t={t}
                                labelKey="OAUTH2_AUTHORIZATION_ADDITIONAL_PARAMS"
                                value={oauth2AuthorizationAdditionalParams}
                                keyName="OAUTH2_AUTHORIZATION_ADDITIONAL_PARAMS"
                                disableControl={disableControl}
                                screen={screen}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                                onInputChange={onInputChange}
                                setter={setOauth2AuthorizationAdditionalParams}
                            />
                        </>
                    )}

                    <InputRow
                        t={t}
                        labelKey="OAUTH2_ACCESS_TOKEN_URL"
                        value={oauth2AccessTokenUrl}
                        keyName="OAUTH2_ACCESS_TOKEN_URL"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2AccessTokenUrl}
                    />

                    <InputRow
                        t={t}
                        labelKey="LBL_CLIENT_ID"
                        value={oauth2ClientId}
                        keyName="OAUTH2_CLIENT_ID"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2ClientId}
                    />

                    <InputRow
                        t={t}
                        labelKey="LBL_CLIENT_SECRET"
                        value={oauth2ClientSecret}
                        keyName="OAUTH2_CLIENT_SECRET"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2ClientSecret}
                    />

                    <InputRow
                        t={t}
                        labelKey="LBL_USERNAME"
                        value={oauth2Username}
                        keyName="OAUTH2_USERNAME"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2Username}
                        wizardLabelKey="OAUTH2_USERNAME_LBL"
                    />

                    <InputRow
                        t={t}
                        labelKey="LBL_WS_PASSWORD"
                        value={oauth2Password}
                        keyName="OAUTH2_PASSWORD"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2Password}
                        onChange={onOauth2PasswordChange}
                        password
                        wizardLabelKey="OAUTH2_PASSWORD_LBL"
                    />

                    <FormControl className="width-100" size="small">
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={t('OAUTH2_CLIENT_AUTHENTICATION')}
                            value={oauth2ClientAuth || ''}
                            disabled={disableControl || false}
                            onChange={(e) => { setOauth2ClientAuth(e.target.value); onInputChange('OAUTH2_CLIENT_AUTHENTICATION', e.target.value); }}
                            className="width-100"
                        >
                            {CLIENT_AUTH_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    <InputRow
                        t={t}
                        labelKey="OAUTH2_SCOPE"
                        value={oauth2Scope}
                        keyName="OAUTH2_SCOPE"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2Scope}
                    />
                    <InputRow
                        t={t}
                        labelKey="OAUTH2_STATE"
                        value={oauth2State}
                        keyName="OAUTH2_STATE"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2State}
                    />
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{t('OAUTH2_ADVANCED')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <InputRow
                        t={t}
                        labelKey="OAUTH2_REFRESH_TOKEN_URL"
                        value={oauth2RefreshTokenUrl}
                        keyName="OAUTH2_REFRESH_TOKEN_URL"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2RefreshTokenUrl}
                    />
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export default OAuth2Fields;
